import { RoomType, RoomCapacity, QuickSelect, QUICK_SELECTS } from '@/components/types'
import { Feature } from '@/pinia/feature'
import { ColorScaleConfig } from '@/utils/colorScale'
import { FilterQueryDefault } from '../shared/Contracts'

export type EntityType = 'COMPLEX' | 'REAL_ESTATE' | 'CONSTRUCTION' | 'LEVEL' | 'SPACE'
export type Resolution = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' | 'HOUR'
export type Interval<T> = { from?: T; to?: T }
export type CoClass = string

export type InformationQuery = {
  from: string
  to: string
  complexIds?: string[]
  constructionIds?: string[]
  levelIds?: string[]
  onlyBusinessHours: boolean
  onlyBusinessDays: boolean
  onlyBookable: boolean
  coClasses?: CoClass[] | null
  numberOfSeats?: Interval<number>[]
}
export type OptionsWithData = {
  complexes: (string | null)[]
  constructions: (string | null)[]
  levels: (string | null)[]
  coClasses: (string | null)[]
  seats: (number | null)[]
}
export type InformationResponse = {
  numberOfSpaces: number
  numberOfConstructions: number
  numberOfHours: number
  optionsWithData: OptionsWithData
}
export type ToplistQuery = {
  toplistOf: EntityType
  filter: FilterQueryDefault
}
export type TimeSeriesQuery = {
  resolution: Resolution
  filter: FilterQueryDefault
}
export type QuotaQuery = {
  filter: FilterQueryDefault
}
export type ToplistValue = {
  id: string
  name: string
  alias?: string
  numberOfSpaces: number
  used: number
  booked: number
  bookedAndUsed: number
}
export type ToplistResponse = {
  from: string
  to: string
  numberOfHours: number
  toplistOf: EntityType
  values: ToplistValue[]
}
export type TimeSeriesValue = {
  date: string
  numberOfHours: number
  used: number
  booked: number
  bookedAndUsed: number
  maxUsed: number
  maxBooked: number
}
export type TimeSeriesResponse = {
  from: string
  to: string
  numberOfHours: number
  numberOfSpaces: number
  numberOfConstructions: number
  resolution: Resolution
  values: TimeSeriesValue[]
  maxNumberOfHours: number
}

export type QuotaValue = {
  numberOfHours: number
  numberOfSpaces: number
}

export type QuotaResponse = {
  from: string
  to: string
  numberOfHours: number
  numberOfSpaces: number
  numberOfConstructions: number
  usage: QuotaValue[]
  booking: QuotaValue[]
}

export type HeatmapQuery = {
  filter: FilterQueryDefault
}

export type HeatmapValue = {
  numberOfHours: number
  used: number
  booked: number | null
  usedAndBooked: number | null
  usedNotBooked: number | null
  bookedNotUsed: number | null
}

export type HeatmapResponse = {
  from: string
  to: string
  dimensions: {
    x: string[]
    y: number[]
  }
  heatmap: HeatmapValue[][]
  numberOfConstructions: number
  numberOfSpaces: number
}
export type KpiType =
  | 'AVERAGE_USAGE'
  | 'AVERAGE_USED_AND_BOOKED'
  | 'MAX_REMAINDER_CAPACITY'
  | 'AVERAGE_NO_USAGE_WHEN_BOOKED'
export const KPI_TYPES: KpiType[] = [
  'AVERAGE_USAGE',
  'AVERAGE_USED_AND_BOOKED',
  'MAX_REMAINDER_CAPACITY',
  'AVERAGE_NO_USAGE_WHEN_BOOKED'
]
export type Kpi = {
  kpi: string
  splitBy: EntityType
  parameters?: {
    cutoff?: number
  }
}
export type KpiQuery = {
  kpis: Kpi[]
  filter: FilterQueryDefault
}
export type KpiValue = {
  value: number
  numberOfSpaces: number
  metadata: Record<string, any>
  splitKey: string
  coClass: string
}
export type KpiResult = {
  kpi: KpiType
  splitBy: EntityType
  summary: {
    value: number
    metadata: {
      numberOfSpaces: number
    }
  }
  values: KpiValue[]

  // Used as a temporary fix to solve ZDB-4802
  valuesByRoomType?: KpiValue[]
  valuesBySplitBy?: KpiValue[]
}
export type KpiResponse = {
  from: string
  to: string
  kpis: KpiResult[]
}

export type LocaleString = {
  [key in string]: string
}

export type FilterType =
  | 'CO_CLASSES'
  | 'ONLY_BUSINESS_HOURS'
  | 'ONLY_BUSINESS_DAYS'
  | 'ONLY_BOOKABLE'
  | 'NUMBER_OF_SEATS'

export type CalendarQuickSelect =
  | 'YESTERDAY'
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'PREVIOUS_QUARTER'
  | 'LAST_MONTH' // TODO: Remove this when backend is updated

const mapping: Record<CalendarQuickSelect, QuickSelect> = {
  YESTERDAY: QUICK_SELECTS[0],
  LAST_7_DAYS: QUICK_SELECTS[1],
  LAST_30_DAYS: QUICK_SELECTS[2],
  LAST_MONTH: QUICK_SELECTS[2], // TODO: Remove this when backend is updated
  PREVIOUS_QUARTER: QUICK_SELECTS[3]
}
export const backendToFronendQuickSelect = (quickSelect: CalendarQuickSelect): QuickSelect =>
  mapping[quickSelect]

export type PlotType =
  | 'OVERVIEW'
  | 'USED_CAPACITY_PER_TIME_UNIT'
  | 'USAGE_AND_BOOKING_OVER_TIME'
  | 'USAGE_AND_BOOKING_PER_ROOM'

export type HeatmapType =
  | 'AVERAGE_USAGE'
  | 'AVERAGE_BOOKING'
  | 'AVERAGE_USAGE_WHEN_BOOKED'
  | 'AVERAGE_USAGE_WHEN_NOT_BOOKED'
  | 'AVERAGE_BOOKING_WHEN_NOT_USED'

export type FilterSettings = {
  type: FilterType
  defaultValue: any
}

export type CalendarSettings = {
  quickSelect: CalendarQuickSelect
}

export type FilterOverride = {
  onlyBusinessHours?: boolean
  onlyBusinessDays?: boolean
  onlyBookable?: boolean
  coClasses?: CoClass[]
  numberOfSeats?: Interval<number>[]
}

export type KpiSettings = {
  kpi: KpiType
  parameters: any
  title: LocaleString
  info: LocaleString
  showOnMapEnabledFor: EntityType[]
  color: string
  colorScale: ColorScaleConfig
  filterOverride: FilterOverride
}

export type PlotSettings = {
  type: PlotType
  title: LocaleString
  filterOverride: FilterOverride
  info?: LocaleString
}

export type HeatmapSettings = {
  type: HeatmapType
  title: LocaleString
  colorMin: string
  colorMax: string
  filterOverride: FilterOverride
  info?: LocaleString
}

export type RealmFeatures = {
  utilization: {
    enabled: boolean
    kpiInformation?: LocaleString
    filters: FilterSettings[]
    calendar: CalendarSettings
    kpis: KpiSettings[]
    plots: PlotSettings[]
    heatmaps: HeatmapSettings[]
  }
}

export type RealmSettings = {
  id: string
  name?: string
  defaultLocale?: string
  config?: RealmFeatures
  features?: Feature
  featureConfig?: Feature
}

export type DefaultSelections = {
  coClasses: string[]
  roomTypes: RoomType[]
  onlyBusinessHours: boolean
  onlyBusinessDays: boolean
  onlyBookable: boolean
  numberOfSeats?: RoomCapacity[]
  defaultQuickSelect: QuickSelect
}
